// Copyright 2022 The Parca Authors
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
// http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var _a;
import { COLOR_PROFILES } from '@parca/functions';
import { createSlice } from '@reduxjs/toolkit';
// Define the initial state using that type
var initialState = {
    colors: {},
    binaries: [],
};
export var FEATURE_TYPES = {
    Runtime: 'Runtime',
    Binary: 'Binary',
    Misc: 'Misc',
};
export var EVERYTHING_ELSE = 'Everything else';
var findAColor = function (colorIndex, colors) {
    return colors[colorIndex];
    // TODO: add some logic to find unallocated colors if this index is already allocated to another feature for better color distribution.
};
var getColorForFeature = function (feature, isDarkMode, colorProfileName) {
    var colors = COLOR_PROFILES[colorProfileName].colors;
    // Add charaters in the feature name to the color map
    var colorIndex = feature === EVERYTHING_ELSE
        ? colors.length - 1
        : feature
            .toLowerCase()
            .split('')
            .reduce(function (acc, char) {
            acc += char.charCodeAt(0);
            return acc;
        }, 0) % (colors.length > 1 ? colors.length - 1 : 1);
    var color = findAColor(colorIndex, colors);
    return !isDarkMode ? color[0] : color[1];
};
export var colorsSlice = createSlice({
    name: 'colors',
    initialState: initialState,
    reducers: {
        // Use the PayloadAction type to declare the contents of `action.payload`
        addColor: function (state, action) {
            var _a;
            state.colors = __assign(__assign({}, state.colors), (_a = {}, _a[action.payload.name] = action.payload.color, _a));
        },
        setFeatures: function (state, action) {
            var _a;
            state.binaries = Object.keys(action.payload.features).filter(function (name) {
                return action.payload.features[name] === FEATURE_TYPES.Binary;
            });
            state.colors = Object.keys(action.payload.features)
                .map(function (feature) {
                return [
                    feature,
                    getColorForFeature(feature, action.payload.isDarkMode, action.payload.colorProfileName),
                ];
            })
                .reduce(function (acc, _a) {
                var _b = __read(_a, 2), feature = _b[0], color = _b[1];
                acc[feature] = color;
                return acc;
            }, (_a = {},
                _a[EVERYTHING_ELSE] = getColorForFeature(EVERYTHING_ELSE, action.payload.isDarkMode, action.payload.colorProfileName),
                _a));
        },
        resetColors: function (state) {
            state.colors = {};
        },
    },
});
export var addColor = (_a = colorsSlice.actions, _a.addColor), resetColors = _a.resetColors, setFeatures = _a.setFeatures;
// Other code such as selectors can use the imported `RootState` type
export var selectStackColors = function (state) { return state.colors.colors; };
export var selectBinaries = function (state) { return state.colors.binaries; };
export default colorsSlice.reducer;
